import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Table, Space, Button, Input, Modal, Card } from 'antd';
import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { GlobalTableConfig, GlobalTableRef, TableAction, TableColumn } from '@/types/table';

const { confirm } = Modal;

/**
 * 全局通用表格组件
 * 支持通过配置动态生成表格、工具栏、操作列等
 */
const GlobalTable = forwardRef<GlobalTableRef, GlobalTableConfig>((props, ref) => {
  const {
    columns,
    dataSource = [],
    rowKey = 'key',
    loading = false,
    pagination = {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: true,
    },
    header,
    toolbar,
    actions,
    actionsWidth = 150,
    actionsFixed = 'right',
    bordered = false,
    size = 'middle',
    scroll,
    rowSelection,
    expandable,
    summary,
    empty,
    onChange,
    cardProps,
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    rowSelection?.selectedRowKeys || []
  );
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // 暴露表格实例方法
  useImperativeHandle(ref, () => ({
    refresh: () => {
      // 触发刷新逻辑（需要父组件配合）
      console.log('Refresh table');
    },
    clearSelection: () => {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      rowSelection?.onChange?.([], []);
    },
    getSelectedRows: () => selectedRows,
    getSelectedKeys: () => selectedRowKeys,
  }));

  /**
   * 处理操作按钮点击
   */
  const handleActionClick = (action: TableAction, record: any, index: number) => {
    if (action.confirm) {
      const title = typeof action.confirm.title === 'function' 
        ? action.confirm.title(record) 
        : action.confirm.title;
      const content = action.confirm.content 
        ? (typeof action.confirm.content === 'function' 
          ? action.confirm.content(record) 
          : action.confirm.content)
        : undefined;

      confirm({
        title,
        content,
        icon: <ExclamationCircleOutlined />,
        okText: action.confirm.okText || '确定',
        cancelText: action.confirm.cancelText || '取消',
        onOk: () => action.onClick?.(record, index),
      });
    } else {
      action.onClick?.(record, index);
    }
  };

  /**
   * 渲染操作列
   */
  const renderActions = (record: any, index: number) => {
    if (!actions || actions.length === 0) return null;

    const visibleActions = actions.filter((action) => {
      if (typeof action.hidden === 'function') {
        return !action.hidden(record);
      }
      return !action.hidden;
    });

    return (
      <Space size="small">
        {visibleActions.map((action) => {
          const isDisabled = typeof action.disabled === 'function' 
            ? action.disabled(record) 
            : action.disabled;

          // 对于删除按钮（danger 类型），只显示红色图标，不要按钮样式
          if (action.danger && action.key === 'delete') {
            return (
              <span
                key={action.key}
                onClick={() => !isDisabled && handleActionClick(action, record, index)}
                style={{
                  color: '#ff4d4f',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  fontSize: '16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) {
                    e.currentTarget.style.opacity = '0.7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {action.label}
              </span>
            );
          }

          return (
            <Button
              key={action.key}
              type={action.type || 'link'}
              danger={action.danger}
              icon={action.icon}
              disabled={isDisabled}
              onClick={() => handleActionClick(action, record, index)}
              size="small"
            >
              {action.label}
            </Button>
          );
        })}
      </Space>
    );
  };

  /**
   * 转换列配置
   */
  const tableColumns = useMemo<ColumnsType<any>>(() => {
    const cols: ColumnsType<any> = columns
      .filter(col => !col.hidden)
      .map((col: TableColumn) => ({
        title: col.title,
        dataIndex: col.dataIndex,
        key: col.key || (typeof col.dataIndex === 'string' ? col.dataIndex : undefined),
        width: col.width,
        fixed: col.fixed,
        align: col.align,
        ellipsis: col.ellipsis,
        sorter: col.sortable,
        filters: col.filters,
        render: col.render,
      }));

    // 添加操作列
    if (actions && actions.length > 0) {
      cols.push({
        title: '操作',
        key: 'actions',
        width: actionsWidth,
        fixed: actionsFixed,
        render: (_: any, record: any, index: number) => renderActions(record, index),
      });
    }

    return cols;
  }, [columns, actions, actionsWidth, actionsFixed]);

  /**
   * 处理分页配置
   */
  const paginationConfig = useMemo(() => {
    if (pagination === false) return false;

    const showTotal = pagination.showTotal === true
      ? (total: number) => `共 ${total} 条记录`
      : pagination.showTotal === false
      ? undefined
      : pagination.showTotal;

    return {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
      showSizeChanger: pagination.showSizeChanger !== false,
      showQuickJumper: pagination.showQuickJumper !== false,
      showTotal,
      pageSizeOptions: pagination.pageSizeOptions || ['10', '20', '50', '100'],
      onChange: pagination.onChange,
    };
  }, [pagination]);

  /**
   * 处理行选择
   */
  const rowSelectionConfig = rowSelection ? {
    type: rowSelection.type || 'checkbox',
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: any[]) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
      rowSelection.onChange?.(keys, rows);
    },
    getCheckboxProps: rowSelection.getCheckboxProps,
  } : undefined;

  /**
   * 处理搜索
   */
  const handleSearch = () => {
    toolbar?.onSearch?.(searchValue);
  };

  /**
   * 处理清空搜索
   */
  const handleClearSearch = () => {
    setSearchValue('');
    toolbar?.onSearch?.('');
  };

  /**
   * 渲染头部
   */
  const renderHeader = () => {
    if (!header) return null;

    return (
      <div style={{ 
        padding: '16px 24px', 
        borderBottom: '1px solid var(--border-primary)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          {header.title && (
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, marginBottom: header.subtitle ? 8 : 0, color: 'var(--text-primary)' }}>
              {header.title}
            </h2>
          )}
          {header.subtitle && (
            <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {header.subtitle}
            </div>
          )}
        </div>
        {header.extra && <div>{header.extra}</div>}
      </div>
    );
  };

  /**
   * 渲染工具栏
   */
  const renderToolbar = () => {
    if (!toolbar) return null;

    return (
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {toolbar.title && (
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{toolbar.title}</div>
          )}
          {toolbar.showSearch && (
            <Space.Compact size="large">
              <Input
                placeholder={toolbar.searchPlaceholder || '请输入搜索关键词'}
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ width: 300 }}
                allowClear
                onClear={handleClearSearch}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
            </Space.Compact>
          )}
          {toolbar.actions && toolbar.actions.length > 0 && (
            <Space>
              {toolbar.actions}
            </Space>
          )}
        </div>
        {toolbar.extra && <div style={{ display: 'flex', gap: 8 }}>{toolbar.extra}</div>}
      </div>
    );
  };

  /**
   * 渲染表格内容
   */
  const renderTable = () => (
    <Table
      columns={tableColumns}
      dataSource={dataSource}
      rowKey={rowKey}
      loading={loading}
      pagination={paginationConfig}
      bordered={bordered}
      size={size}
      scroll={scroll}
      rowSelection={rowSelectionConfig}
      expandable={expandable}
      summary={summary}
      locale={{
        emptyText: empty || '暂无数据',
      }}
      onChange={onChange}
      className="global-table"
    />
  );

  // 如果有 header 配置，使用 Card 包装
  if (header) {
    return (
      <Card 
        className={cardProps?.className || 'bg-bg-secondary border-slate-600'}
        styles={cardProps?.styles || {
          header: { 
            background: 'var(--bg-tertiary)', 
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          },
          body: {
            padding: 0
          }
        }}
      >
        {renderHeader()}
        <div style={{ padding: '16px 24px' }}>
          {renderToolbar()}
          {renderTable()}
        </div>
      </Card>
    );
  }

  // 否则直接渲染
  return (
    <>
      {renderToolbar()}
      {renderTable()}
    </>
  );
});

GlobalTable.displayName = 'GlobalTable';

export default GlobalTable;

